import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Download, Clock, CheckCircle, AlertTriangle, Trash2, RefreshCw, Plus, FileUp } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import { ConfirmDialog } from '../components/ui/Modal';
import { api, handleApiError } from '@/services/client';

interface Backup {
  id: string;
  name: string;
  size: string;
  sizeBytes: number;
  date: Date;
  type: 'auto' | 'manual';
  status: 'complete' | 'in-progress' | 'failed';
}

interface AutoBackupSettings {
  enabled: boolean;
  frequency: string;
  time_of_day: string;
}

function normalizeBackup(raw: Record<string, unknown>): Backup {
  const created = raw.created_at ?? raw.date ?? raw.createdAt;
  const sizeBytes = Number(raw.size_bytes ?? raw.sizeBytes ?? 0);
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    name: String(raw.name ?? 'Backup'),
    size: String(raw.size ?? (sizeBytes > 0 ? `${sizeBytes} B` : '—')),
    sizeBytes,
    date: created ? new Date(String(created)) : new Date(),
    type: (raw.type === 'auto' ? 'auto' : 'manual') as Backup['type'],
    status: (raw.status === 'in-progress' || raw.status === 'failed' ? raw.status : 'complete') as Backup['status'],
  };
}

function formatRelative(date: Date): string {
  const diff = Date.now() - date.getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatStorage(bytes: number): string {
  if (bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  if (kb >= 1) return `${kb.toFixed(1)} KB`;
  return `${bytes} B`;
}

function scheduleLabel(settings: AutoBackupSettings): string {
  const freq = settings.frequency.charAt(0).toUpperCase() + settings.frequency.slice(1);
  return `${freq} at ${settings.time_of_day}`;
}

const BackupCenter: React.FC = () => {
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [autoSettings, setAutoSettings] = useState<AutoBackupSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [isRestoringUpload, setIsRestoringUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadBackups = useCallback(() => {
    api.get('/backups')
      .then((r) => {
        const rows = r.data?.backups ?? r.data ?? [];
        setBackups(Array.isArray(rows) ? rows.map((b) => normalizeBackup(b as Record<string, unknown>)) : []);
      })
      .catch((err) => setError(handleApiError(err)));
  }, []);

  useEffect(() => {
    loadBackups();
    api.get('/backups/settings')
      .then((r) => setAutoSettings(r.data as AutoBackupSettings))
      .catch(() => setAutoSettings({ enabled: false, frequency: 'weekly', time_of_day: '03:00' }));
  }, [loadBackups]);

  const storageBytes = backups.reduce((sum, b) => sum + b.sizeBytes, 0);
  const lastBackup = backups[0]?.date;
  const autoCount = backups.filter((b) => b.type === 'auto').length;

  const createBackup = () => {
    setError(null);
    setSuccess(null);
    setIsBackingUp(true);
    api.post('/backups/create', {})
      .then((r) => {
        setBackups((prev) => [normalizeBackup(r.data as Record<string, unknown>), ...prev]);
        setSuccess('Backup created successfully.');
      })
      .catch((err) => setError(handleApiError(err)))
      .finally(() => setIsBackingUp(false));
  };

  const downloadBackup = async (backup: Backup) => {
    setError(null);
    try {
      const res = await api.get(`/backups/${backup.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${backup.name.replace(/\s+/g, '-').toLowerCase()}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const confirmRestore = () => {
    if (!pendingRestoreId) return;
    setError(null);
    setSuccess(null);
    api.post(`/backups/${pendingRestoreId}/restore`)
      .then((r) => {
        const created = Number(r.data?.sessions_created ?? 0);
        const tabs = Number(r.data?.tabs_in_backup ?? 0);
        setSuccess(
          created > 0
            ? `Restore complete — ${created} workspace session(s) recreated (${tabs} tabs in archive).`
            : `Backup verified — archive contains ${tabs} tab(s). No new sessions were needed.`,
        );
        setPendingRestoreId(null);
      })
      .catch((err) => {
        setError(handleApiError(err));
        setPendingRestoreId(null);
      });
  };

  const onSelectUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingUploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmRestoreUpload = async () => {
    if (!pendingUploadFile) return;
    setError(null);
    setSuccess(null);
    setIsRestoringUpload(true);
    try {
      const form = new FormData();
      form.append('file', pendingUploadFile);
      const r = await api.post('/backups/restore-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = Number(r.data?.sessions_created ?? 0);
      const skipped = Number(r.data?.sessions_skipped ?? 0);
      const tabs = Number(r.data?.tabs_in_backup ?? 0);
      setSuccess(
        `Restore complete — ${created} session(s) recreated, ${skipped} kept as-is (merge), ${tabs} tab(s) in archive.`,
      );
      loadBackups();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsRestoringUpload(false);
      setPendingUploadFile(null);
    }
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    setError(null);
    api.delete(`/backups/${pendingDeleteId}`)
      .then(() => {
        setBackups((prev) => prev.filter((b) => b.id !== pendingDeleteId));
        setPendingDeleteId(null);
      })
      .catch((err) => {
        setError(handleApiError(err));
        setPendingDeleteId(null);
      });
  };

  const toggleAutoBackup = () => {
    if (!autoSettings) return;
    const next = { ...autoSettings, enabled: !autoSettings.enabled };
    api.put('/backups/settings', next)
      .then((r) => {
        setAutoSettings(r.data as AutoBackupSettings);
        setSuccess(next.enabled ? 'Auto-backup enabled.' : 'Auto-backup disabled.');
      })
      .catch((err) => setError(handleApiError(err)));
  };

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'complete': return { bg: 'bg-success-500' };
      case 'in-progress': return { bg: 'bg-warning-500' };
      case 'failed': return { bg: 'bg-error-500' };
      default: return { bg: 'bg-khiimori-bg-tertiary' };
    }
  };

  return (
    <PageLayout
      title="Backup Center"
      description="Backup and restore your data safely"
      icon={Upload}
    >
      {(error || success) && (
        <div className={`mb-4 rounded-lg border px-4 py-2 text-sm ${
          error
            ? 'border-red-500/40 bg-red-500/10 text-red-300'
            : 'border-success-500/40 bg-success-500/10 text-success-300'
        }`}>
          {error || success}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Backups', value: backups.length, textClass: 'text-primary-500' },
          { label: 'Storage Used', value: formatStorage(storageBytes), textClass: 'text-info-500' },
          { label: 'Last Backup', value: lastBackup ? formatRelative(lastBackup) : '—', textClass: 'text-success-500' },
          { label: 'Auto Backups', value: autoCount, textClass: 'text-khiimori-accent-violet' },
        ].map((stat, index) => (
          <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card>
              <div className="text-center">
                <div className={`text-2xl font-bold ${stat.textClass}`}>
                  {stat.value}
                </div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={onSelectUploadFile}
        className="hidden"
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-khiimori-text-primary">Backup History</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isRestoringUpload}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-khiimori-bg-tertiary text-khiimori-text-secondary text-sm font-medium hover:bg-khiimori-bg-elevated disabled:opacity-60"
            title="Restore from a downloaded backup archive"
          >
            <FileUp className="h-4 w-4" />
            {isRestoringUpload ? 'Restoring…' : 'Restore from file'}
          </button>
          <button
            type="button"
            onClick={createBackup}
            disabled={isBackingUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {isBackingUp ? 'Creating…' : 'Create backup'}
          </button>
        </div>
      </div>

      {backups.length === 0 ? (
        <p className="text-sm text-khiimori-text-muted py-8 text-center">No backups yet. Create one to snapshot your workspace data.</p>
      ) : (
        <div className="space-y-4 mb-6">
          {backups.map((backup, index) => (
            <motion.div key={backup.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
              <Card>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className={`p-3 rounded-xl shrink-0 ${getStatusClasses(backup.status).bg}`}>
                      {backup.status === 'complete' ? <CheckCircle className="h-6 w-6 text-khiimori-text-primary" /> :
                       backup.status === 'in-progress' ? <RefreshCw className="h-6 w-6 text-khiimori-text-primary animate-spin" /> :
                       <AlertTriangle className="h-6 w-6 text-khiimori-text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-khiimori-text-primary truncate">{backup.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-khiimori-text-secondary">
                        <span>{backup.size}</span>
                        <span>•</span>
                        <span>{backup.date.toLocaleDateString()}</span>
                        <span className={`px-2 py-0.5 rounded ${backup.type === 'auto' ? 'bg-primary-500/20 text-primary-500' : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'}`}>
                          {backup.type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => downloadBackup(backup)}
                      className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"
                      title="Download backup"
                    >
                      <Download className="h-5 w-5 text-primary-400" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingRestoreId(backup.id)}
                      className="flex items-center space-x-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Restore</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteId(backup.id)}
                      className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"
                      title="Delete backup"
                    >
                      <Trash2 className="h-5 w-5 text-error-500" />
                    </button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Card className={`border ${autoSettings?.enabled ? 'bg-gradient-glass border-primary-400/30 dark:border-primary-400/20' : 'border-khiimori-border'}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-xl ${autoSettings?.enabled ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'}`}>
                {autoSettings?.enabled
                  ? <CheckCircle className="h-6 w-6 text-khiimori-text-primary" />
                  : <Clock className="h-6 w-6 text-khiimori-text-muted" />}
              </div>
              <div>
                <h3 className="font-bold text-khiimori-text-primary">
                  {autoSettings?.enabled ? 'Auto-Backup Enabled' : 'Auto-Backup Disabled'}
                </h3>
                <p className="text-sm text-khiimori-text-secondary">
                  {autoSettings?.enabled
                    ? `Your data is scheduled for backup ${scheduleLabel(autoSettings).toLowerCase()}`
                    : 'Enable automatic weekly backups to protect your workspace'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleAutoBackup}
              disabled={!autoSettings}
              className="px-4 py-2 rounded-lg bg-khiimori-bg-tertiary text-sm text-khiimori-text-secondary hover:bg-khiimori-bg-elevated disabled:opacity-50"
            >
              {autoSettings?.enabled ? 'Disable' : 'Enable'}
            </button>
          </div>
        </Card>
      </motion.div>

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={confirmDelete}
        title="Delete backup"
        message="Delete this backup permanently? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={Boolean(pendingRestoreId)}
        onClose={() => setPendingRestoreId(null)}
        onConfirm={confirmRestore}
        title="Restore backup"
        message="Restore merges this backup into your workspace. Sessions that already exist are kept as-is (never overwritten), and only missing ones are recreated, so newer data is safe."
        confirmText="Restore (merge)"
        cancelText="Cancel"
        variant="info"
      />

      <ConfirmDialog
        isOpen={Boolean(pendingUploadFile)}
        onClose={() => setPendingUploadFile(null)}
        onConfirm={confirmRestoreUpload}
        title="Restore from file"
        message={`Restore from "${pendingUploadFile?.name ?? 'archive'}"? This merges the archive into your workspace — existing sessions are kept as-is (never overwritten) and only missing ones are recreated.`}
        confirmText="Restore (merge)"
        cancelText="Cancel"
        variant="info"
      />
    </PageLayout>
  );
};

export default BackupCenter;
